import { useAuth } from '../context/AuthContext';
import { API, apiRequest } from './api';
import { ApiError } from './errors';

export function usePurchaseService() {
  async function initiatePurchase(purchaseData = {}) {
    const token = localStorage.getItem('token');
    try {
      const response = await apiRequest(API.INITIATE_PURCHASE, {
        method: 'POST',
        token,
        body: purchaseData,
      });
      const contentType = response.headers.get('Content-Type');
      if (contentType && contentType.includes('application/json')) {
        return await response.json();
      } else {
        console.warn('Response has no JSON body.');
        return {};
      }
    } catch (error) {
      console.error('Error during purchase initiation:', error);
      // Ensure errors are status-code based.
      if (error?.name === 'ApiError') throw error;
      throw error;
    }
  }

  async function initiateMultipleSinglePurchase(purchaseData = {}) {
    const token = localStorage.getItem('token');
    try {
      const response = await apiRequest(API.INITIATE_MULTIPLE_SINGLE_PURCHASE, {
        method: 'POST',
        token,
        body: purchaseData,
      });
      const contentType = response.headers.get('Content-Type');
      if (contentType && contentType.includes('application/json')) {
        return await response.json();
      } else {
        console.warn('Response has no JSON body.');
        return {};
      }
    } catch (error) {
      console.error('Error during purchase initiation:', error);
      throw error;
    }
  }

  async function initiateSurveyUnlock(purchaseData = {}) {
    const token = localStorage.getItem('token');
    try {
      const response = await apiRequest(API.INITIATE_SURVEY_UNLOCK, {
        method: 'POST',
        token,
        body: purchaseData,
      });
      const contentType = response.headers.get('Content-Type');
      if (contentType && contentType.includes('application/json')) {
        return await response.json();
      } else {
        console.warn('Response has no JSON body.');
        return {};
      }
    } catch (error) {
      console.error('Error during survey unlock initiation:', error);
      throw error;
    }
  }

  async function redeemPurchase(purchaseCode, clinicianEmail) {
    const token = localStorage.getItem('token');
    try {
      return await apiRequest(API.REDEEM, {
        method: 'POST',
        token,
        body: {
          purchaseCode,
          userIdentifier: clinicianEmail,
        },
      });
    } catch (e) {
      // apiRequest already throws ApiError(context=apiRequest). Re-map to purchase context.
      if (e?.name === 'ApiError') {
        throw new ApiError('purchaseRedeem', e.status);
      }
      throw e;
    }
  }

  /**
   * Fetch purchase(s) by Stripe sessionId.
   * @param {string} sessionId
   * @param {string} token
   * @returns {Promise<any>}
   */
  async function getPurchaseBySessionId(sessionId, token) {
    if (!token)
      throw new Error(
        'Authentication token is required to check payment status.',
      );
    try {
      const response = await apiRequest(
        `${API.GET_PURCHASE_BY_SESSION_ID}/${sessionId}`,
        {
          method: 'GET',
          token,
        },
      );
      return response.json();
    } catch (e) {
      if (e?.name === 'ApiError') {
        throw new ApiError('purchaseInitiate', e.status);
      }
      throw e;
    }
  }

  /**
   * Check unlock status by surveyResponseId
   * @param {string} surveyResponseId
   * @param {string} token
   * @returns {Promise<{surveyResponseId: string, isUnlocked: boolean}>}
   */
  async function checkSurveyUnlockStatus(surveyResponseId, token) {
    if (!token)
      throw new Error(
        'Authentication token is required to check unlock status.',
      );
    if (!surveyResponseId) throw new Error('surveyResponseId is required.');

    const url = `${API.SURVEY_UNLOCK_STATUS}/${encodeURIComponent(
      surveyResponseId,
    )}`;
    const response = await apiRequest(url, {
      method: 'GET',
      token,
    });
    const data = await response.json();
    return data;
  }

  return {
    initiatePurchase,
    initiateMultipleSinglePurchase,
    initiateSurveyUnlock,
    redeemPurchase,
    getPurchaseBySessionId,
    checkSurveyUnlockStatus,
  };
}
